import { Fragment, useEffect, useState } from 'react';
import {
  DevHubBox,
  DevHubButton,
  DevHubCircularProgress,
  DevHubDynamicIcon,
  DevHubModal,
  DevHubTooltip,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { gql, useMutation } from '@apollo/client';
import { DocumentNode } from 'graphql';
import {
  AppRole,
  CreateResponsibilityInput,
  ResourceIdentityType,
  ResponsibleParty,
  RolesQueryVariables,
} from '../types/RoleTypes';
import { PartySearch } from '../../common/PartySearch';
import { RoleSearch } from './RoleSearch';
import { Submitted } from './Submitted';
import { Link } from '@backstage/core-components';
import { isSupportRole, supportRoleUrl } from './RoleHelper';

const CreateMutation = gql`
  mutation CreateAppResponsibility($input: CreateResponsibilityInput!) {
    resources {
      responsibilities {
        createResponsibility(input: $input) {
          success
          responsibility {
            responsibilityId
          }
        }
      }
    }
  }
`;

type Props = {
  role?: AppRole;
  appId: number;
  rolesQuery: DocumentNode;
  rolesVariables: RolesQueryVariables;
  rolesSearch: boolean;
  buttonText: string;
  canEditRoles: (roleId: number | undefined) => boolean;
};

/**
 * Modal pop up for modifying a responsibility on a role
 * @param props Props of the component
 * @param props.role The role being modified
 * @param props.rolesQuery Handle on the query for fetching roles
 * @param props.rolesSearch Boolean whether to display role search box or not
 * @param props.buttonText Text to display on the button
 */
export function ModifyResponsibility(props: Props) {
  const [open, setOpen] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [selectedRole, setSelectedRole] = useState<AppRole>({} as AppRole);
  const [selectedParty, setSelectedParty] = useState<ResponsibleParty>(
    {} as ResponsibleParty,
  );

  useEffect(() => {
    if (props?.role) {
      setSelectedRole(props?.role);
    }
  }, [props.role]);

  const [createResponsibility, { data, loading, error }] = useMutation(
    CreateMutation,
    {
      refetchQueries: [
        { query: props.rolesQuery, variables: props.rolesVariables },
        'GetAppRolesAndAccessPolicy',
      ],
    },
  );

  const handleOpenModal = () => {
    setOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedParty({} as ResponsibleParty);
    setSubmitted(false);
    setOpen(false);
  };

  const roleId = props?.role?.id ? props?.role?.id : selectedRole?.id;
  const handleSubmit = () => {
    const id =
      selectedParty?.__typename === 'User'
        ? selectedParty?.login
        : selectedParty?.name;

    if (id && roleId) {
      const input: CreateResponsibilityInput = {
        roleId: roleId,
        applicationId: props.appId,
        identityId: id,
        identityType:
          selectedParty?.__typename === 'User'
            ? ResourceIdentityType.User
            : ResourceIdentityType.Group,
      };
      setSubmitted(true);
      createResponsibility({ variables: { input: input } });
    }
  };

  const validate = () => {
    if (
      selectedParty?.__typename?.length <= 0 ||
      selectedParty?.login === 'clear' ||
      !roleId ||
      !props?.canEditRoles(selectedRole.id) ||
      loading
    ) {
      return true;
    }
    return false;
  };

  const renderRoleMessage = () => {
    if (isSupportRole(selectedRole.name)) {
      return (
        <DevHubBox>
          <DevHubTypography variant="body1">
            To modify this role please use the intake form located{' '}
            <Link to={supportRoleUrl(selectedRole.name)} target="_blank">
              Here
            </Link>
          </DevHubTypography>
        </DevHubBox>
      );
    }
    return (
      <DevHubBox>
        <DevHubTypography variant="body1">
          You do not have the correct AD group or application role permissions
          to manage the selected role.
        </DevHubTypography>
      </DevHubBox>
    );
  };

  return (
    <Fragment>
      <DevHubTooltip title="Add new roles and responsibilities">
        <DevHubButton
          variant="contained"
          buttonColor="black"
          size="small"
          height="large"
          startIcon={
            loading ? (
              <DevHubCircularProgress thickness={8} size={16} />
            ) : (
              <DevHubDynamicIcon iconName="Add" />
            )
          }
          onClick={handleOpenModal}
          href="#"
          disabled={!props?.canEditRoles(undefined)}
        >
          {props.buttonText}
        </DevHubButton>
      </DevHubTooltip>
      <DevHubModal
        open={open}
        onClose={handleCloseModal}
        title={
          props.role?.displayName
            ? props.role?.displayName
            : 'Add Responsibility'
        }
      >
        {submitted ? (
          <Submitted
            error={error}
            submitted={submitted}
            loading={loading}
            handleClose={handleCloseModal}
            success={
              data?.resources?.responsibilities?.createResponsibility?.success
            }
            errorMessage="Error creating responsibility."
          />
        ) : (
          <>
            {props.rolesSearch && (
              <RoleSearch setSelectedRole={setSelectedRole} />
            )}

            {selectedRole && !props?.canEditRoles(selectedRole.id) ? (
              renderRoleMessage()
            ) : (
              <PartySearch
                helperText="Party you wish to add to the role"
                groupEnabled
                setSelectedParty={setSelectedParty}
              />
            )}
            <DevHubBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <DevHubButton
                variant="contained"
                color="primary"
                onClick={handleCloseModal}
              >
                Cancel
              </DevHubButton>
              <DevHubButton
                variant="contained"
                color="primary"
                disabled={validate()}
                onClick={handleSubmit}
              >
                Submit
              </DevHubButton>
            </DevHubBox>
          </>
        )}
      </DevHubModal>
    </Fragment>
  );
}
